import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import americanExpress from "assets/images/payment/american-express.png";
import mastercard from "assets/images/payment/mastercard.png";
import visa from "assets/images/payment/visa.png";
import Button from "components/Button";
import Checkbox from "components/Checkbox";
import { routes } from "constants/routes";
import { TERMS_OF_SERVICE } from "constants/signup";
import { useUserFeature } from "hooks/useUserFeature";
import { RootState } from "store";
import StripeCheckoutButton from "./StripeCheckoutButton";
import styles from "./style.module.scss";
import { PlanDetails, SubscriptionPlanType } from "helpers/subscriptionPlans";

interface IBillingStripeContent {
  subscriptionPlanType: SubscriptionPlanType;
  planDetails: PlanDetails;
}

export default function BillingStripeContent({
  subscriptionPlanType,
  planDetails
}: Readonly<IBillingStripeContent>) {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const { isShipper, isDispatcher } = useUserFeature();

  const {
    account: { type: accountType }
  } = useSelector((state: RootState) => state.authState);

  function goToJoinHaulPay() {
    navigate(routes.HAULPAY_JOIN);
  }

  const { planLabelOther, planPrice, planPriceByYear, planKey } = planDetails;

  return (
    <>
      <div className={styles.pricingPlanSummarySection}>
        <h3 className={styles.pricingPlanSummaryTitle}>Order Summary</h3>

        <div className={styles.pricingPlanSummaryBoxGroup}>
          <div className={styles.pricingPlanSummaryBox1}>
            <div className="font-bold">
              <span className="capitalize">{subscriptionPlanType}</span>
              <span>${planPrice}</span>
            </div>
            <div>
              <span>1 user login</span>
              <span className="text-info">{planLabelOther}</span>
            </div>
            <p>{planPriceByYear}/year/user</p>
          </div>

          <div className={styles.pricingPlanSummaryBox2}>
            <span>Today’s Charge</span>
            <span>${planPrice}</span>
          </div>

          <div className={styles.pricingPlanSummaryBox3}>
            <p className="text-sm font-bold">Charges auto-renew</p>
            <p>
              (Unless cancelled in settings before the next <br /> renewal date)
            </p>
            <p className="text-error/70">You can cancel your subscription settings at any time.</p>
            {subscriptionPlanType === "annually" && <p>We’ll remind you before the period ends.</p>}

            <div className={styles.pricingPlanSummaryBox3ImgWrapper}>
              <img src={visa} className="w-8" alt="Visa Card Logo" />
              <img src={mastercard} className="w-8" alt="Mastercard Card Logo" />
              <img src={americanExpress} className="w-8" alt="American Express Card Logo" />
            </div>
            <p>
              All amounts shown in <b>U.S Dollars</b>
            </p>
          </div>
        </div>

        <Checkbox
          checked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
          wrapperClass="text-xs">
          I agree to the{" "}
          <a
            href={TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary">
            HaulPay Terms of Service
          </a>
        </Checkbox>

        <StripeCheckoutButton
          label="Continue"
          accountType={accountType}
          plan={planKey}
          planAmount={planPrice}
          disabled={!isChecked}
        />
      </div>

      <div className={styles.pricingPlanJoin}>
        <p className="text-xs">
          An alternative to subscribing for a paid subscription is to join our Haul Pay digital
          factoring service. Once approved for Haul Pay you will have full access to all software,
          load boards and additional tools like credit checking, instant invoice payment advances
          and more.
        </p>

        {!isShipper && !isDispatcher && (
          <Button
            variant="outline"
            className={styles.pricingPlanJoinButton}
            onClick={goToJoinHaulPay}>
            Join Haul Pay instead
          </Button>
        )}
      </div>
    </>
  );
}
